import Service, { inject as service } from '@ember/service';
import { computed } from '@ember/object';
import { notEmpty, filterBy } from '@ember/object/computed';
import DS from 'ember-data';
import CustomForm from 'cio/models/custom-form';
import { CustomFormStatus } from 'cio/models/custom-form';

import FeaturesService from 'ember-feature-flags/services/features';
import AlertsService from 'cio/services/alerts';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import EnvironmentService from './environment';
import { generateFormHTMLFingerprint } from 'cio/utils/forms';

// All HTML input element types except:
// - "submit", "button", "image": they don't contain form values
// - "password": we don't use them
//
// + special values for other HTML tags used as form controls, i.e. "select"/"textarea"
type ScannableInputType =
  | Exclude<HTMLInputElement['type'], 'submit' | 'button' | 'image' | 'password'>
  | 'select'
  | 'textarea';

export type ScannedFormField = {
  name: string;

  type: ScannableInputType;
};

export type ScannedForm = {
  id: string;
  name: string;
  inputs: ScannedFormField[];
  generatedHTMLFingerprint: string;
};

// mirrors `forms.js` helper function with same name in https://github.com/customerio/sdk:
function generateDefaultInputName(input: ScannedFormField, currentInputIndexesByType: Record<string, number>): string {
  let { type } = input;
  type = type.trim() || 'text';

  currentInputIndexesByType[type] = currentInputIndexesByType[type] || 0;
  let name = `${type}_${currentInputIndexesByType[type] + 1}`;
  currentInputIndexesByType[type]++;

  return name;
}

export default class CustomFormsService extends Service {
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service alerts!: AlertsService;
  @service environment!: EnvironmentService;
  @service features!: FeaturesService;
  @service store!: DS.Store;

  forms: CustomForm[] = [];
  initialized = false;
  private initializing = false;

  @filterBy('forms', 'isDraft') draftForms!: CustomForm[];
  @filterBy('forms', 'isConnected') connectedForms!: CustomForm[];
  @filterBy('forms', 'isConnectedOrDraft') connectedOrDraftForms!: CustomForm[];
  @filterBy('forms', 'isDisconnected') disconnectedForms!: CustomForm[];

  @notEmpty('connectedOrDraftForms') hasConnectedOrDraftForms!: boolean;

  async initialize(forceRefresh = false) {
    if (!forceRefresh && (this.initialized || this.initializing)) {
      return;
    }

    // set to avoid multiple API requests if initialize() is called many times at once
    this.initializing = true;

    try {
      await this.fetchForms();
      this.set('initialized', true);
    } finally {
      this.initializing = false;
    }
  }

  async fetchForms() {
    let forms = await this.store.query('custom-form', {});

    this.set('forms', forms.toArray());

    return forms;
  }

  // called when switching workspaces:
  unload() {
    this.setProperties({
      initialized: false,
      forms: [],
    });
  }

  @computed('environment.current.isEmailIdentified')
  get canConnectForms() {
    return !this.environment.current.isEmailIdentified; // disable in legacy Email-only workspaces
  }

  async scanURLForForms(url: string) {
    let normalizedURL = new URL(url).toString();

    let scannedFormsRaw = (
      await this.api.request(`custom_forms/scan_for_forms?url=${encodeURIComponent(normalizedURL)}`, {
        pathScope: 'global',
        ignoreErrors: true,
      })
    ).scanned_forms;

    let scannedForms: ScannedForm[] = [];
    for (let [, sf] of scannedFormsRaw.entries()) {
      // transform to set default names for input elements missing them: SquareSpace forms scenario
      let currentInputIndexesByType: Record<string, number> = {};

      sf.inputs = sf.inputs.map((input: ScannedFormField) => {
        let { name } = input;
        if (!name) {
          name = generateDefaultInputName(input, currentInputIndexesByType);
        }

        return { ...input, name };
      });

      let generatedHTMLFingerprint: string;
      try {
        generatedHTMLFingerprint = await generateFormHTMLFingerprint(
          sf.inputs.filter((inp: ScannedFormField) => inp.type !== 'hidden').mapBy('name'),
        );
      } catch (e) {
        // fallback in case browser doesn't support SubtleCrypto.digest():
        generatedHTMLFingerprint = '';
      }

      scannedForms.push({ ...sf, generatedHTMLFingerprint });
    }

    return scannedForms.map((sf) => {
      return {
        ...sf,
        inputs: sf.inputs.uniqBy('name'),
      };
    });
  }

  async saveNewForm(form: CustomForm) {
    if (!form.isNew) {
      throw new Error('Must be a new custom form');
    } else if (form.status !== CustomFormStatus.Draft) {
      throw new Error('New form status must be draft');
    }

    return this._updateForm(form);
  }

  async connectForm(form: CustomForm) {
    if (form.status !== CustomFormStatus.Connected) {
      throw new Error('New form status must be connected');
    }

    return this._updateForm(form, CustomFormStatus.Disconnected);
  }

  async disconnectForm(form: CustomForm) {
    if (form.status !== CustomFormStatus.Disconnected) {
      throw new Error('New form status must be disconnected');
    }

    return this._updateForm(form, CustomFormStatus.Connected);
  }

  async updateFormSettings(form: CustomForm, skipAlert = false) {
    return this._updateForm(form, form.status, skipAlert);
  }

  private async _updateForm(form: CustomForm, originalStatus?: CustomFormStatus, skipAlert = false) {
    let isConnectingExistingForm =
      originalStatus === CustomFormStatus.Disconnected && form.status !== CustomFormStatus.Disconnected;
    let isDisconnecting =
      originalStatus === CustomFormStatus.Connected && form.status === CustomFormStatus.Disconnected;

    try {
      await form.save();

      if (skipAlert) {
        return;
      }

      let formNoun = form.isTypeform ? 'Typeform form' : 'Custom form';

      if (isConnectingExistingForm) {
        this.alerts.success('customForms.form.connect.success', `${formNoun} reconnected`);
      } else if (isDisconnecting) {
        this.alerts.success('customForms.form.disconnect.success', `${formNoun} disconnected`);
      } else if (originalStatus) {
        this.alerts.success('customForms.form.updated.success', `${formNoun} settings updated`);
      }
    } catch (e) {
      this.apiErrors.report(e);
      throw e;
    }
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    'custom-forms': CustomFormsService;
  }
}
